/**
 * @file common.tsx
 * @brief Small shared pieces for Super Admin → Analytics (W22): section
 *        headings with definitions, money cells with the exact value on
 *        hover, "–" with its reason, and the error / empty notes.
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import type { ReactNode } from "react";
import { InfoTip } from "@/components/ui/InfoTip";
import { ApiClientError } from "@/shared/api/client";
import { cn } from "@/shared/theme/cn";
import { exactMinor, formatMoney } from "../businessMath";
import type { Currency } from "../types";

export function SectionHeading({
  title,
  help,
  info,
  right,
}: {
  title: string;
  /** Small help text under the title (a definition, in a line). */
  help?: ReactNode;
  /** The API's full definition, behind an (i). */
  info?: string;
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
      <div className="min-w-0 space-y-0.5">
        <h3 className="flex items-center gap-1.5 text-base font-semibold tracking-tight">
          {title}
          {info ? <InfoTip label={title} text={info} /> : null}
        </h3>
        {help ? <p className="max-w-3xl text-xs leading-relaxed text-muted-foreground">{help}</p> : null}
      </div>
      {right}
    </div>
  );
}

/** Money as the API gave it: formatted, the raw minor value on hover. */
export function MoneyText({
  minor,
  currency,
  className,
}: {
  minor: number | null | undefined;
  currency: Currency | string;
  className?: string;
}) {
  return (
    <span
      className={cn("tabular-nums", minor != null && minor < 0 && "text-destructive", className)}
      title={exactMinor(minor, currency)}
    >
      {formatMoney(minor, currency)}
    </span>
  );
}

/** "–" with the reason the API (or the contract) gives for a null. */
export function NullValue({ reason, className }: { reason: string; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <span aria-label="Not available">–</span>
      <InfoTip label="why this is empty" text={reason} />
    </span>
  );
}

export function ErrorNote({ error, what }: { error: unknown; what: string }) {
  const msg =
    error instanceof ApiClientError
      ? `${error.message}${error.code ? ` (${error.code})` : ""}`
      : error instanceof Error
        ? error.message
        : "";
  return (
    <p className="px-4 py-6 text-sm text-destructive md:px-5">
      Couldn't load {what}.{msg ? ` ${msg}` : ""}
    </p>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="px-4 py-6 text-center text-sm text-muted-foreground md:px-5">{children}</p>;
}

/** A plain data table in the panel's hairline style; scrolls sideways on a phone. */
export function DataTable({
  head,
  children,
  caption,
}: {
  head: { label: ReactNode; align?: "left" | "right"; className?: string }[];
  children: ReactNode;
  caption?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] text-sm">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead className="text-xs uppercase tracking-wide text-muted-foreground">
          <tr className="border-t border-border/60">
            {head.map((h, i) => (
              <th
                key={i}
                className={cn(
                  "whitespace-nowrap px-4 py-2.5 font-medium md:px-5",
                  h.align === "right" ? "text-right" : "text-left",
                  h.className,
                )}
              >
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export const tdClass = "px-4 py-2.5 md:px-5";
export const tdNumClass = "px-4 py-2.5 text-right tabular-nums whitespace-nowrap md:px-5";
